import {
  Box,
  Button,
  Chip,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from "@mui/material"
import { alpha } from "@mui/material/styles"
import type { Schedule } from "../../interface/schedule"
import { newMoscowRule } from "../../utils/placementDraft"
import { ScheduleDateTimePicker } from "./scheduleDateTimePicker"

export interface ScheduleEditorProps {
  /** Local Moscow date strings; scheduleFromDraft performs wire conversion on submit. */
  value: Schedule
  onChange: (schedule: Schedule) => void
  readOnly?: boolean
  canWrite?: boolean
}
const weekDays = ["Вс", "Пн", "Вт", "Ср", "Чт", "Пт", "Сб"]
const presets = {
  weekDays: [
    { label: "Каждый день", values: [] },
    { label: "Будни", values: [2, 3, 4, 5, 6] },
    { label: "Выходные", values: [1, 7] },
  ],
  hour: [
    { label: "Каждый час", values: [] },
    ...[2, 3, 4].map((step) => ({
      label: `Каждые ${step} часа`,
      short: `${step} ч`,
      values: Array.from({ length: 24 / step }, (_, index) => index * step),
    })),
  ],
  minute: [
    { label: "Каждую минуту", values: [] },
    { label: "4 раза в час", short: "4×/ч", values: [0, 15, 30, 45] },
    { label: "2 раза в час", short: "2×/ч", values: [0, 30] },
    { label: "1 раз в час", short: "1×/ч", values: [0] },
  ],
} satisfies Record<
  string,
  { label: string; short?: string; values: number[] }[]
>
const zoneLabel = (tz: number) => {
  if (tz === 3) return "Время по Москве"
  const minutes = Math.abs(tz * 60)
  const offset = Number.isInteger(minutes)
    ? `${String(Math.floor(minutes / 60)).padStart(2, "0")}:${String(minutes % 60).padStart(2, "0")}`
    : `${Math.abs(tz)} ч`
  return `UTC${tz >= 0 ? "+" : "−"}${offset}`
}
export function ScheduleEditor({
  value,
  onChange,
  readOnly,
  canWrite,
}: ScheduleEditorProps) {
  const disabled = !!readOnly || canWrite === false
  const update = (index: number, rule: Schedule["schedules"][number]) => {
    if (disabled) return
    onChange({
      ...value,
      schedules: value.schedules.map((current, i) =>
        i === index ? rule : current,
      ),
    })
  }
  return (
    <Stack spacing={2}>
      {value.schedules.map((rule, index) => (
        <Stack
          key={index}
          component="fieldset"
          spacing={1.5}
          sx={{
            m: 0,
            p: 1.5,
            border: 1,
            borderColor: (theme) => alpha(theme.palette.text.primary, 0.14),
            borderRadius: 1,
            minWidth: 0,
          }}
        >
          <Typography component="legend" variant="caption">
            Правило {index + 1}
          </Typography>
          {rule.type === "interval" ? (
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: {
                  xs: "minmax(0, 1fr)",
                  sm: "repeat(2, minmax(0, 1fr))",
                },
                maxWidth: 372,
                gap: 1.5,
              }}
            >
              <TextField
                select
                size="small"
                label="Единица"
                value={rule.period}
                disabled={disabled}
                onChange={(event) =>
                  update(index, {
                    ...rule,
                    period: event.target.value as "m" | "h" | "d",
                  })
                }
              >
                <MenuItem value="m">Минуты (1–59)</MenuItem>
                <MenuItem value="h">Часы (1–23)</MenuItem>
                <MenuItem value="d">Дни (1–31)</MenuItem>
              </TextField>
              <TextField
                size="small"
                type="number"
                label={`Интервал ${index + 1}`}
                value={Number.isFinite(rule.value) ? rule.value : ""}
                disabled={disabled}
                inputProps={{
                  min: 1,
                  max: { m: 59, h: 23, d: 31 }[rule.period],
                  step: 1,
                }}
                onChange={(event) =>
                  update(index, {
                    ...rule,
                    value:
                      event.target.value === ""
                        ? NaN
                        : Number(event.target.value),
                  })
                }
              />
            </Box>
          ) : (
            <>
              <Typography variant="caption" color="text.secondary">
                {zoneLabel(rule.tz)}
              </Typography>
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: {
                    xs: "minmax(0, 1fr)",
                    sm: "repeat(3, minmax(0, 1fr))",
                  },
                  gap: 1.5,
                }}
              >
                {(
                  [
                    ["weekDays", "Дни", 1, 7, "Каждый день"],
                    ["hour", "Часы", 0, 23, "Каждый час"],
                    ["minute", "Минуты", 0, 59, "Каждую минуту"],
                  ] as const
                ).map(([field, label, min, max, empty]) => (
                  <Stack key={field} spacing={0.75} sx={{ minWidth: 0 }}>
                    <TextField
                      select
                      size="small"
                      label={label}
                      value={rule[field]}
                      disabled={disabled}
                      SelectProps={{
                        multiple: true,
                        displayEmpty: true,
                        renderValue: (selected) =>
                          (selected as number[]).length
                            ? (selected as number[])
                                .map((number) =>
                                  field === "weekDays"
                                    ? weekDays[number - 1]
                                    : String(number).padStart(2, "0"),
                                )
                                .join(", ")
                            : empty,
                      }}
                      InputLabelProps={{ shrink: true }}
                      onChange={(event) => {
                        const selected = event.target
                          .value as unknown as number[]
                        if (Array.isArray(selected))
                          update(index, { ...rule, [field]: [...selected] })
                      }}
                    >
                      {Array.from(
                        { length: max - min + 1 },
                        (_, i) => i + min,
                      ).map((number) => (
                        <MenuItem key={number} value={number}>
                          {field === "weekDays" ? weekDays[number - 1] : number}
                        </MenuItem>
                      ))}
                    </TextField>
                    <Stack direction="row" useFlexGap flexWrap="wrap" gap={0.5}>
                      {presets[field].map((preset) => (
                        <Chip
                          key={preset.label}
                          component="button"
                          type="button"
                          size="small"
                          variant="outlined"
                          label={
                            "short" in preset ? preset.short : preset.label
                          }
                          aria-label={preset.label}
                          disabled={disabled}
                          onClick={() =>
                            update(index, {
                              ...rule,
                              [field]: [...preset.values],
                            })
                          }
                        />
                      ))}
                    </Stack>
                  </Stack>
                ))}
              </Box>
            </>
          )}
          <Button
            size="small"
            sx={{ alignSelf: "flex-start" }}
            disabled={disabled}
            onClick={() =>
              onChange({
                ...value,
                schedules: value.schedules.filter((_, i) => i !== index),
              })
            }
          >
            Удалить правило {index + 1}
          </Button>
        </Stack>
      ))}
      <Stack direction="row" useFlexGap flexWrap="wrap">
        <Button
          disabled={disabled}
          onClick={() =>
            onChange({
              ...value,
              schedules: [...value.schedules, newMoscowRule()],
            })
          }
        >
          Добавить расписание
        </Button>
        <Button
          disabled={disabled}
          onClick={() =>
            onChange({
              ...value,
              schedules: [
                ...value.schedules,
                { type: "interval", period: "m", value: 5 },
              ],
            })
          }
        >
          Добавить интервал
        </Button>
      </Stack>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "minmax(0, 1fr)",
            sm: "repeat(2, minmax(0, 1fr))",
          },
          gap: 1.5,
        }}
      >
        {(
          [
            ["dateStart", "Начало"],
            ["dateEnd", "Окончание"],
          ] as const
        ).map(([field, label]) => (
          <ScheduleDateTimePicker
            key={field}
            label={label}
            value={value[field]}
            disabled={disabled}
            onChange={(next) => onChange({ ...value, [field]: next })}
          />
        ))}
      </Box>
    </Stack>
  )
}
